import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import {
  usePatchLoanAgencyFieldsMutation,
  usePatchLoanBorrowersMutation,
  usePatchLoanDscrMutation,
  usePatchLoanInvestorProgramFieldsMutation,
  usePatchLoanPropertyMutation,
  usePatchLoanRefinanceFieldsMutation,
  type LoanMissingField,
} from '@/apis/extended/loanApplicationApi';
import type { LoanApplication } from '@/types/LoanApplicationType';

type Props = {
  open: boolean;
  application: LoanApplication;
  missingFields: LoanMissingField[];
  onClose: () => void;
  /** Called after every value saved successfully — the caller retries submit. */
  onSaved: () => void;
  saving?: boolean;
};

type Values = Record<string, string>;

const isNumeric = (f: LoanMissingField) =>
  f.input === 'currency' || f.input === 'number';

/**
 * Collects exactly the fields the backend says are missing for submit, saves
 * each to its own section endpoint, then hands back to the caller to submit.
 * Replaces a toast listing fields the borrower's form may not even show.
 */
export const SubmitMissingInfoModal = ({
  open,
  application,
  missingFields,
  onClose,
  onSaved,
  saving: externalSaving,
}: Props) => {
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const [patchBorrowers] = usePatchLoanBorrowersMutation();
  const [patchProperty] = usePatchLoanPropertyMutation();
  const [patchRefinance] = usePatchLoanRefinanceFieldsMutation();
  const [patchDscr] = usePatchLoanDscrMutation();
  const [patchAgency] = usePatchLoanAgencyFieldsMutation();
  const [patchInvestor] = usePatchLoanInvestorProgramFieldsMutation();

  useEffect(() => {
    if (open) {
      setValues({});
      setErrors({});
    }
  }, [open, missingFields]);

  const editable = missingFields.filter((f) => f.section && f.input !== 'link');
  const notEditable = missingFields.filter(
    (f) => !f.section || f.input === 'link'
  );

  const set = (key: string, v: string) => {
    setValues((prev) => ({ ...prev, [key]: v }));
    setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    editable.forEach((f) => {
      if (f.input === 'borrowerName') {
        if (!values.firstName?.trim()) next.firstName = 'Required';
        if (!values.lastName?.trim()) next.lastName = 'Required';
        return;
      }
      const v = (values[f.key] || '').trim();
      if (!v) next[f.key] = 'Required';
      else if (isNumeric(f) && !(Number(v.replace(/[,$\s]/g, '')) > 0)) {
        next[f.key] = 'Enter an amount greater than 0';
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    const id = application._id;
    const bySection: Record<string, Record<string, unknown>> = {};
    editable.forEach((f) => {
      if (f.input === 'borrowerName') return;
      const raw = (values[f.key] || '').trim();
      const value = isNumeric(f) ? Number(raw.replace(/[,$\s]/g, '')) : raw;
      bySection[f.section as string] = {
        ...(bySection[f.section as string] || {}),
        [f.field as string]: value,
      };
    });

    try {
      const calls: Promise<unknown>[] = [];
      if (editable.some((f) => f.input === 'borrowerName')) {
        const rows = [...(application.borrowers || [])];
        const i = Math.max(
          0,
          rows.findIndex((b) => b?.role === 'primary')
        );
        const primary = { ...(rows[i] || { role: 'primary' }) } as any;
        primary.firstName = values.firstName.trim();
        primary.lastName = values.lastName.trim();
        primary.role = primary.role || 'primary';
        rows[i] = primary;
        calls.push(patchBorrowers({ id, borrowers: rows as any }).unwrap());
      }
      Object.entries(bySection).forEach(([section, body]) => {
        if (section === 'property') {
          calls.push(patchProperty({ id, property: body as any }).unwrap());
        } else if (section === 'refinance-fields') {
          calls.push(
            patchRefinance({ id, refinanceFields: body as any }).unwrap()
          );
        } else if (section === 'dscr') {
          calls.push(patchDscr({ id, dscrFields: body }).unwrap());
        } else if (section === 'agency-fields') {
          calls.push(patchAgency({ id, ...(body as any) }).unwrap());
        } else if (section === 'investor-program-fields') {
          calls.push(
            patchInvestor({ id, investorProgramFields: body as any }).unwrap()
          );
        }
      });
      await Promise.all(calls);
      onSaved();
    } catch (e: any) {
      toast.error(e?.data?.error || 'Could not save — please try again');
    } finally {
      setSaving(false);
    }
  };

  const busy = saving || !!externalSaving;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>A few details before underwriting</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Underwriting needs {missingFields.length === 1 ? 'this' : 'these'} to
          review your file. Enter {missingFields.length === 1 ? 'it' : 'them'}{' '}
          here and we&apos;ll submit right away.
        </Typography>
        <Stack spacing={2.5}>
          {editable.map((f) => {
            if (f.input === 'borrowerName') {
              return (
                <Box key={f.key}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {f.label}
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <TextField
                      label="First name"
                      fullWidth
                      value={values.firstName || ''}
                      onChange={(e) => set('firstName', e.target.value)}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                    />
                    <TextField
                      label="Last name"
                      fullWidth
                      value={values.lastName || ''}
                      onChange={(e) => set('lastName', e.target.value)}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                    />
                  </Stack>
                </Box>
              );
            }
            if (f.input === 'select') {
              return (
                <TextField
                  key={f.key}
                  select
                  label={f.label}
                  fullWidth
                  value={values[f.key] || ''}
                  onChange={(e) => set(f.key, e.target.value)}
                  error={!!errors[f.key]}
                  helperText={errors[f.key] || f.helper}
                >
                  {(f.options || []).map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </TextField>
              );
            }
            return (
              <TextField
                key={f.key}
                label={f.label}
                fullWidth
                value={values[f.key] || ''}
                onChange={(e) => set(f.key, e.target.value)}
                error={!!errors[f.key]}
                helperText={errors[f.key] || f.helper}
                inputMode={isNumeric(f) ? 'decimal' : 'text'}
                InputProps={
                  f.input === 'currency'
                    ? {
                        startAdornment: (
                          <InputAdornment position="start">$</InputAdornment>
                        ),
                      }
                    : undefined
                }
              />
            );
          })}
          {notEditable.map((f) => (
            <Alert key={f.key} severity="info">
              <strong>{f.label}:</strong> {f.helper}
            </Alert>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={busy || editable.length === 0}
        >
          {busy ? 'Submitting…' : 'Save & submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitMissingInfoModal;
